/* eslint-disable @typescript-eslint/no-explicit-any */
import { BehaviorSubject, Observable, of } from 'rxjs';
import { ApiService, DefaultHttpHeaders, HeadersEnum, HttpInputParametersType } from '../api.service';
import { Injectable } from '@angular/core';
import { delay, finalize, map, switchMap, take, tap } from 'rxjs/operators';
import { Mocks } from '@core/services/mocks/mocks';
import { select } from '@ngrx/store';
import { getInitFlow } from '@store/flow-data/init-flow';
import { InitFlow } from '@core/models/swagger/initFlow';
import { ListEntry, ListKind, ParameterResult } from '@core/models/swagger/bff';

@Injectable({
  providedIn: 'root',
})
export class CatalogService extends ApiService {
  mocked = false;

  private readonly loadingSubjects: Record<string, BehaviorSubject<boolean>> = {
    getDeductibles: new BehaviorSubject<boolean>(false),
    getGenders: new BehaviorSubject<boolean>(false),
    getTitles: new BehaviorSubject<boolean>(false),
    postPartnerSearch: new BehaviorSubject<boolean>(false),
    getOccupations: new BehaviorSubject<boolean>(false),
    getMaritalStatus: new BehaviorSubject<boolean>(false),
    getClaimsAmount: new BehaviorSubject<boolean>(false),
    getPreviousInsurances: new BehaviorSubject<boolean>(false),
  };

  readonly getDeductiblesLoading$ = this.loadingSubjects.getDeductibles.asObservable();
  readonly getGendersLoading$ = this.loadingSubjects.getGenders.asObservable();
  readonly getTitlesLoading$ = this.loadingSubjects.getTitles.asObservable();
  readonly getOccupationsLoading$ = this.loadingSubjects.getOccupations.asObservable();
  readonly getMaritalStatusLoading$ = this.loadingSubjects.getMaritalStatus.asObservable();
  readonly getClaimsAmountLoading$ = this.loadingSubjects.getClaimsAmount.asObservable();
  readonly getPreviousInsurances$ = this.loadingSubjects.getPreviousInsurances.asObservable();

  // Stored data to prevent retrieving the information twice
  deductibles?: DeductiblesResponse;
  genders?: GendersResponse;
  titles?: TitlesResponse;
  occupations?: OccupationsResponse;
  maritalStatus?: MaritalStatusResponse;
  claimsAmount?: ClaimsAmountResponse;
  previousInsurances?: PreviousInsurancesResponse;

  private executeGetRequest<TResponse>({
    endpoint,
    queryParams,
    requestKey,
    mocked,
    headers,
  }: {
    endpoint: string;
    queryParams?: HttpInputParametersType;
    requestKey: string;
    mocked?: boolean;
    headers?: DefaultHttpHeaders;
  }): Observable<TResponse> {
    this.loadingSubjects[requestKey].next(true);

    if (this.mocked || mocked) {
      return Mocks[requestKey]().pipe(
        delay(2000),
        finalize(() => this.loadingSubjects[requestKey].next(false))
      );
    }

    this.store.pipe(select(getInitFlow), take(1)).subscribe((initFlow: InitFlow) => {
      headers = {
        ...headers,
        [HeadersEnum.X_CORRELATION_ID]: initFlow?.correlationId?.toString(),
      };
    });

    return this.get<TResponse>({
      suffix: endpoint,
      queryParams,
      headers,
    }).pipe(finalize(() => this.loadingSubjects[requestKey].next(false)));
  }

  private executePostRequest<TRequest, TResponse>({
    endpoint,
    body,
    requestKey,
    mocked,
    headers = {},
  }: {
    endpoint: string;
    body: TRequest;
    requestKey: string;
    mocked?: boolean;
    headers?: DefaultHttpHeaders;
  }): Observable<TResponse> {
    this.loadingSubjects[requestKey].next(true);

    if (this.mocked || mocked) {
      return Mocks[requestKey]().pipe(
        delay(2000),
        finalize(() => this.loadingSubjects[requestKey].next(false))
      );
    }

    const correlationKey = HeadersEnum.X_CORRELATION_ID;

    if (headers[correlationKey]) {
      return this.post<TRequest, TResponse>({ suffix: endpoint, body, headers }).pipe(
        finalize(() => this.loadingSubjects[requestKey].next(false))
      );
    }

    return this.store.pipe(
      select(getInitFlow),
      take(1),
      switchMap((initFlow: InitFlow) => {
        const enrichedHeaders = {
          ...headers,
          [correlationKey]: initFlow?.correlationId?.toString(),
        };
        return this.post<TRequest, TResponse>({ suffix: endpoint, body, headers: enrichedHeaders });
      }),
      finalize(() => this.loadingSubjects[requestKey].next(false))
    );
  }

  getDeductibles(): Observable<DeductiblesResponse> {
    return this.deductibles === undefined
      ? this.executeGetRequest<DeductiblesResponse>({
          endpoint: '/catalogs/deductibles',
          requestKey: 'getDeductibles',
        }).pipe(tap(deductibles => (this.deductibles = deductibles)))
      : of(this.deductibles);
  }

  getGenders(): Observable<GendersResponse> {
    return this.genders === undefined
      ? this.executeGetRequest<GendersResponse>({
          endpoint: '/catalogs/genders',
          requestKey: 'getGenders',
        }).pipe(tap(genders => (this.genders = genders)))
      : of(this.genders);
  }

  getTitles(): Observable<TitlesResponse> {
    return this.titles === undefined
      ? this.executeGetRequest<TitlesResponse>({
          endpoint: '/catalogs/titles',
          requestKey: 'getTitles',
        }).pipe(tap(titles => (this.titles = titles)))
      : of(this.titles);
  }

  getOccupations(): Observable<OccupationsResponse> {
    return this.occupations === undefined
      ? this.executeGetRequest<OccupationsResponse>({
          endpoint: '/catalogs/occupations',
          requestKey: 'getOccupations',
        }).pipe(tap(occupations => (this.occupations = occupations)))
      : of(this.occupations);
  }

  getMaritalStatus(): Observable<MaritalStatusResponse> {
    return this.maritalStatus === undefined
      ? this.executeGetRequest<MaritalStatusResponse>({
          endpoint: '/catalogs/maritalStatus',
          requestKey: 'getMaritalStatus',
        }).pipe(tap(maritalStatus => (this.maritalStatus = maritalStatus)))
      : of(this.maritalStatus);
  }

  getClaimsAmount(): Observable<ClaimsAmountResponse> {
    return this.claimsAmount === undefined
      ? this.executeGetRequest<ClaimsAmountResponse>({
          endpoint: '/catalogs/claimAmount',
          requestKey: 'getClaimsAmount',
          mocked: false,
        }).pipe(tap(claimsAmount => (this.claimsAmount = claimsAmount)))
      : of(this.claimsAmount);
  }

  getPreviousInsurances(): Observable<PreviousInsurancesResponse> {
    return this.previousInsurances === undefined
      ? this.executePostRequest<PreviousInsurancesRequest, PreviousInsurancesResponseUnmapped>({
          endpoint: '/list',
          requestKey: 'getPreviousInsurances',
          body: { listKind: 'PhvInsurer' },
          mocked: false,
        }).pipe(
          map(previousInsurancesUnmapped =>
            previousInsurancesUnmapped.list.map(input => ({
              parameterId: input.key,
              parameterName: input.value,
            }))
          ),
          tap(previousInsurances => (this.previousInsurances = previousInsurances))
        )
      : of(this.previousInsurances);
  }
}

export type DeductiblesResponse = Array<ParameterResult>;
export type GendersResponse = Array<ParameterResult>;
export type TitlesResponse = Array<ParameterResult>;
export type OccupationsResponse = Array<ParameterResult>;
export type MaritalStatusResponse = Array<ParameterResult>;
export type ClaimsAmountResponse = Array<ParameterResult>;
export type PreviousInsurancesResponse = Array<ParameterResult>;
export type PreviousInsurancesResponseUnmapped = { list: Array<ListEntry> };
export type PreviousInsurancesRequest = { listKind: ListKind };
